import { useState, useMemo } from "react";
import {
  Crown, Users, Calculator, ChevronDown, ChevronUp,
  ArrowRight, Check, Receipt, BookOpen, BarChart3,
  GraduationCap, Megaphone, ShieldCheck, Zap, Clock,
  AlertTriangle, Mail
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";

/* ════════════════════════════════════════════
   DATA
   ════════════════════════════════════════════ */

const TIERS = [
  { min: 1, max: 5, price: 199 },
  { min: 6, max: 10, price: 169 },
  { min: 11, max: 50, price: 149 },
  { min: 51, max: Infinity, price: 129 },
];

const ADDONS = [
  {
    id: "compta",
    name: "Compta",
    price: 249,
    icon: Receipt,
    color: "emerald",
    benefits: [
      "Rapprochement bancaire automatique",
      "Integration Pennylane native",
      "Tableaux de bord financiers temps reel",
    ],
  },
  {
    id: "marketing",
    name: "Marketing",
    price: 99,
    icon: Megaphone,
    color: "violet",
    benefits: [
      "Campagnes email & SMS automatisees",
      "Scoring et segmentation leads",
      "Analytics conversion et ROI",
    ],
  },
  {
    id: "formation",
    name: "Formation",
    price: 199,
    icon: GraduationCap,
    color: "amber",
    benefits: [
      "Modules e-learning integres",
      "Suivi des competences equipe",
      "Certifications et attestations",
    ],
  },
];

const FAQS = [
  {
    q: "Y a-t-il un essai gratuit ou un plan Free ?",
    a: "Non. SAN-IA est une offre premium avec onboarding accompagne par notre equipe. Ce choix garantit une mise en place reussie et un accompagnement de qualite des le premier jour.",
  },
  {
    q: "Comment comptez-vous les utilisateurs actifs ?",
    a: "Un utilisateur actif est un utilisateur qui s'est connecte au moins une fois pendant la periode de facturation en cours. Seuls les utilisateurs actifs sont factures. Minimum 1 siege si le tenant est actif.",
  },
  {
    q: "Le prix par siege change-t-il en fonction du palier ?",
    a: "Oui. Le prix par siege est determine par le nombre total d'utilisateurs actifs. Le tarif du palier atteint s'applique a l'ensemble des sieges. Exemple : 8 utilisateurs actifs = 8 x 169 EUR.",
  },
  {
    q: "Puis-je ajouter des utilisateurs en cours de mois ?",
    a: "Oui. Les nouveaux utilisateurs sont factures au prorata du mois restant. Le palier tarifaire est recalcule en temps reel.",
  },
  {
    q: "Puis-je desactiver un module en cours de mois ?",
    a: "La desactivation prend effet a la fin du cycle de facturation en cours. Le module reste accessible jusqu'a cette date.",
  },
  {
    q: "Que se passe-t-il si le paiement echoue ?",
    a: "En cas d'impaye, l'acces aux modules concernes est immediatement bloque. L'acces est retabli automatiquement des reception du paiement.",
  },
  {
    q: "L'audit initial est-il obligatoire ?",
    a: "Non. Seuls les frais de mise en service (990 EUR) sont obligatoires. L'audit & consulting (2 000 EUR) est optionnel mais recommande pour optimiser le deploiement.",
  },
  {
    q: "Comment fonctionne l'onboarding ?",
    a: "L'onboarding est entierement gere par notre equipe. Apres paiement des frais de mise en service, nous configurons votre environnement, importons vos donnees et formons vos equipes.",
  },
];

/* ════════════════════════════════════════════
   HELPERS
   ════════════════════════════════════════════ */

function getTierPrice(seats) {
  for (const t of TIERS) {
    if (seats >= t.min && seats <= t.max) return t.price;
  }
  return TIERS[TIERS.length - 1].price;
}

/* ════════════════════════════════════════════
   SUB-COMPONENTS
   ════════════════════════════════════════════ */

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-800 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group"
        data-testid="faq-item"
      >
        <span className="text-sm sm:text-base text-slate-200 font-medium pr-4 group-hover:text-white transition-colors">{q}</span>
        {open
          ? <ChevronUp className="w-4 h-4 text-slate-500 shrink-0" />
          : <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />}
      </button>
      {open && (
        <p className="pb-5 text-sm text-slate-400 leading-relaxed -mt-1">{a}</p>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════
   MAIN PAGE
   ════════════════════════════════════════════ */

export default function PricingPage() {
  const [seats, setSeats] = useState(5);
  const [selectedAddons, setSelectedAddons] = useState({
    compta: false,
    marketing: false,
    formation: false,
  });

  const toggleAddon = (id) =>
    setSelectedAddons((prev) => ({ ...prev, [id]: !prev[id] }));

  const estimate = useMemo(() => {
    const tierPrice = getTierPrice(seats);
    const seatsCost = seats * tierPrice;
    const addonsCost = ADDONS.reduce(
      (sum, a) => sum + (selectedAddons[a.id] ? a.price : 0),
      0
    );
    return { tierPrice, seatsCost, addonsCost, monthly: seatsCost + addonsCost };
  }, [seats, selectedAddons]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* ─── HERO ─── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(99,102,241,.18),transparent)]" />
        <div className="relative max-w-5xl mx-auto px-5 pt-20 pb-16 text-center">
          <Badge variant="outline" className="mb-5 border-indigo-700/50 text-indigo-400 text-xs tracking-wider px-3 py-1">
            PREMIUM ONLY
          </Badge>
          <h1 data-testid="pricing-hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight bg-gradient-to-b from-white to-slate-400 bg-clip-text text-transparent leading-tight">
            Offre Premium
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            CRM multi-tenant avec IA integree, concu pour les entreprises du batiment.
            Onboarding accompagne, facturation transparente, zero surprise.
          </p>
          <a
            href="mailto:contact@sani-adapt.fr?subject=Demande%20de%20d%C3%A9mo%20SAN-IA"
            data-testid="cta-demo"
            className="inline-flex items-center gap-2 mt-8 px-7 py-3 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm transition-all hover:shadow-lg hover:shadow-indigo-600/20"
          >
            Demander une demo <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* ─── TIER PRICING TABLE ─── */}
      <section className="max-w-5xl mx-auto px-5 py-16">
        <h2 className="text-base sm:text-lg font-semibold text-slate-300 mb-1">Tarifs par siege</h2>
        <p className="text-sm text-slate-500 mb-6">
          Le tarif du palier atteint s'applique a <span className="text-slate-300 font-medium">l'ensemble</span> de vos sieges actifs.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="tier-table">
          {TIERS.map((t, i) => {
            const active = seats >= t.min && seats <= t.max;
            return (
              <div
                key={i}
                className={`relative rounded-xl border p-5 transition-all ${
                  active
                    ? "border-indigo-600 bg-indigo-950/40 ring-1 ring-indigo-600/40"
                    : "border-slate-800 bg-slate-900/50 hover:border-slate-700"
                }`}
              >
                {active && (
                  <div className="absolute -top-2.5 left-4">
                    <Badge className="bg-indigo-600 text-white text-[10px] px-2">ACTIF</Badge>
                  </div>
                )}
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">
                  {t.max === Infinity ? `${t.min}+` : `${t.min} – ${t.max}`} utilisateurs
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-white">{t.price}</span>
                  <span className="text-sm text-slate-500">EUR</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">/ utilisateur / mois</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ─── ADD-ONS ─── */}
      <section className="max-w-5xl mx-auto px-5 py-16">
        <h2 className="text-base sm:text-lg font-semibold text-slate-300 mb-1">Modules complementaires</h2>
        <p className="text-sm text-slate-500 mb-6">Activables par tenant. Factures mensuellement, desactivation en fin de cycle.</p>

        <div className="grid md:grid-cols-3 gap-4" data-testid="addons-grid">
          {ADDONS.map((a) => {
            const Icon = a.icon;
            const colorMap = {
              emerald: { border: "border-emerald-800", bg: "bg-emerald-950/40", icon: "text-emerald-400", ring: "ring-emerald-600/40" },
              violet: { border: "border-violet-800", bg: "bg-violet-950/40", icon: "text-violet-400", ring: "ring-violet-600/40" },
              amber: { border: "border-amber-800", bg: "bg-amber-950/40", icon: "text-amber-400", ring: "ring-amber-600/40" },
            };
            const c = colorMap[a.color];
            const selected = selectedAddons[a.id];
            return (
              <div
                key={a.id}
                data-testid={`addon-${a.id}`}
                className={`rounded-xl border p-5 transition-all cursor-pointer ${
                  selected ? `${c.border} ${c.bg} ring-1 ${c.ring}` : "border-slate-800 bg-slate-900/50 hover:border-slate-700"
                }`}
                onClick={() => toggleAddon(a.id)}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${selected ? c.bg : "bg-slate-800"}`}>
                      <Icon className={`w-4 h-4 ${selected ? c.icon : "text-slate-500"}`} />
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-white">{a.name}</div>
                      <div className="text-xs text-slate-500">{a.price} EUR / mois</div>
                    </div>
                  </div>
                  <Switch checked={selected} onCheckedChange={() => toggleAddon(a.id)} />
                </div>
                <ul className="space-y-2">
                  {a.benefits.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-xs text-slate-400">
                      <Check className="w-3.5 h-3.5 text-slate-600 mt-0.5 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* ─── CALCULATOR ─── */}
      <section className="max-w-5xl mx-auto px-5 py-16">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-6 sm:p-8" data-testid="calculator">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-indigo-950 border border-indigo-800 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-white">Estimateur de cout</h2>
              <p className="text-xs text-slate-500">Estimation indicative — le montant final depend de l'usage reel</p>
            </div>
          </div>

          {/* Slider */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm text-slate-400">Utilisateurs actifs</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={200}
                  value={seats}
                  onChange={(e) => setSeats(Math.max(1, Math.min(200, parseInt(e.target.value) || 1)))}
                  data-testid="seats-input"
                  className="w-16 text-center bg-slate-800 border border-slate-700 rounded-lg text-white text-sm py-1 font-mono"
                />
              </div>
            </div>
            <input
              type="range"
              min={1}
              max={80}
              value={Math.min(seats, 80)}
              onChange={(e) => setSeats(parseInt(e.target.value))}
              data-testid="seats-slider"
              className="w-full accent-indigo-500 h-1.5"
            />
            <div className="flex justify-between text-[10px] text-slate-600 mt-1">
              <span>1</span><span>10</span><span>20</span><span>50</span><span>80</span>
            </div>
          </div>

          {/* Add-ons toggles */}
          <div className="flex flex-wrap gap-2 mb-8">
            {ADDONS.map((a) => (
              <button
                key={a.id}
                onClick={() => toggleAddon(a.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                  selectedAddons[a.id]
                    ? "bg-indigo-950 border-indigo-700 text-indigo-300"
                    : "bg-slate-800 border-slate-700 text-slate-500 hover:text-slate-300"
                }`}
              >
                {selectedAddons[a.id] && <Check className="w-3 h-3" />}
                {a.name} ({a.price} EUR)
              </button>
            ))}
          </div>

          {/* Result */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-xl bg-slate-800/60 border border-slate-700 p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Sieges</div>
              <div className="text-2xl font-bold text-white font-mono" data-testid="calc-seats-total">
                {estimate.seatsCost.toLocaleString("fr-FR")} <span className="text-sm text-slate-500">EUR</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {seats} x {estimate.tierPrice} EUR/mois
              </div>
            </div>
            <div className="rounded-xl bg-slate-800/60 border border-slate-700 p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Modules</div>
              <div className="text-2xl font-bold text-white font-mono" data-testid="calc-addons-total">
                {estimate.addonsCost.toLocaleString("fr-FR")} <span className="text-sm text-slate-500">EUR</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {Object.entries(selectedAddons).filter(([,v]) => v).length} module(s) actif(s)
              </div>
            </div>
            <div className="rounded-xl bg-indigo-950/50 border border-indigo-800 p-4">
              <div className="text-xs text-indigo-400 uppercase tracking-wider mb-1">Total mensuel estime</div>
              <div className="text-2xl font-bold text-white font-mono" data-testid="calc-monthly-total">
                {estimate.monthly.toLocaleString("fr-FR")} <span className="text-sm text-slate-500">EUR</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Hors frais de mise en service
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SETUP & ONBOARDING ─── */}
      <section className="max-w-5xl mx-auto px-5 py-16">
        <h2 className="text-base sm:text-lg font-semibold text-slate-300 mb-1">Mise en service & onboarding</h2>
        <p className="text-sm text-slate-500 mb-6">Frais uniques payes avant l'activation de votre environnement.</p>

        <div className="grid sm:grid-cols-2 gap-4" data-testid="setup-section">
          <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center">
                <Zap className="w-4 h-4 text-slate-400" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">Mise en service</div>
                <Badge variant="outline" className="text-[10px] border-red-800 text-red-400 mt-0.5">OBLIGATOIRE</Badge>
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-2">990 <span className="text-sm text-slate-500">EUR</span></div>
            <ul className="space-y-2 text-xs text-slate-400">
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-slate-600 mt-0.5 shrink-0" />Configuration de votre environnement</li>
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-slate-600 mt-0.5 shrink-0" />Import de vos donnees existantes</li>
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-slate-600 mt-0.5 shrink-0" />Formation initiale de l'equipe admin</li>
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-slate-600 mt-0.5 shrink-0" />Support prioritaire pendant 30 jours</li>
            </ul>
          </div>

          <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-slate-400" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">Audit & Consulting</div>
                <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-500 mt-0.5">OPTIONNEL</Badge>
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-2">2 000 <span className="text-sm text-slate-500">EUR</span></div>
            <ul className="space-y-2 text-xs text-slate-400">
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-slate-600 mt-0.5 shrink-0" />Audit complet de vos processus metier</li>
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-slate-600 mt-0.5 shrink-0" />Plan de deploiement personnalise</li>
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-slate-600 mt-0.5 shrink-0" />Configuration avancee des workflows IA</li>
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-slate-600 mt-0.5 shrink-0" />2 sessions de consulting strategique</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ─── BILLING RULES ─── */}
      <section className="max-w-5xl mx-auto px-5 py-16">
        <h2 className="text-base sm:text-lg font-semibold text-slate-300 mb-6">Regles de facturation</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="billing-rules">
          {[
            { icon: Users, title: "Utilisateurs actifs", desc: "Seuls les utilisateurs connectes pendant la periode de facturation sont comptes. Minimum 1 siege par tenant actif." },
            { icon: Clock, title: "Cycle mensuel", desc: "Facturation le 1er de chaque mois pour le mois en cours. Les modules actives en cours de mois sont factures au prorata." },
            { icon: BarChart3, title: "Palier unique", desc: "Le prix par siege est determine par le nombre total d'utilisateurs actifs. Le palier s'applique a l'ensemble des sieges." },
            { icon: Zap, title: "Activation module", desc: "Les modules complementaires sont factures en mois plein des l'activation. Desactivation effective en fin de cycle." },
            { icon: AlertTriangle, title: "Impaye", desc: "En cas de non-paiement, l'acces aux modules concernes est immediatement bloque. Retablissement automatique apres paiement." },
            { icon: Crown, title: "Onboarding admin", desc: "Chaque tenant est cree et configure par l'administrateur plateforme. Aucune inscription publique — onboarding premium uniquement." },
          ].map((r, i) => (
            <div key={i} className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
              <div className="flex items-center gap-2 mb-2">
                <r.icon className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-300">{r.title}</span>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="max-w-3xl mx-auto px-5 py-16">
        <h2 className="text-base sm:text-lg font-semibold text-slate-300 mb-6">Questions frequentes</h2>
        <div className="border border-slate-800 rounded-xl bg-slate-900/50 px-6" data-testid="faq-section">
          {FAQS.map((f, i) => (
            <FaqItem key={i} q={f.q} a={f.a} />
          ))}
        </div>
      </section>

      {/* ─── CTA FOOTER ─── */}
      <section className="max-w-5xl mx-auto px-5 pt-8 pb-20 text-center">
        <div className="rounded-2xl border border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950 p-8 sm:p-12">
          <Crown className="w-8 h-8 text-indigo-500 mx-auto mb-4" />
          <h2 className="text-lg sm:text-xl font-bold text-white mb-2">Pret a transformer votre gestion ?</h2>
          <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">
            Contactez-nous pour planifier votre onboarding personnalise.
          </p>
          <a
            href="mailto:contact@sani-adapt.fr?subject=Demande%20de%20d%C3%A9mo%20SAN-IA"
            data-testid="cta-footer"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm transition-all hover:shadow-lg hover:shadow-indigo-600/20"
          >
            <Mail className="w-4 h-4" /> Demander une demo
          </a>
        </div>
      </section>
    </div>
  );
}
